/* eslint-disable @typescript-eslint/no-explicit-any */
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostBinding, HostListener, Inject, NgZone, OnDestroy } from '@angular/core';
import { IndividualConfig, ToastPackage, ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppFacade } from '@app/app.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { SnackbarType } from '@design-system/components/molecules/snackbar/entities/snackbar.types';

@Component({
  selector: 'app-travel-toast',
  styleUrls: ['./travel-toast.component.sass'],
  templateUrl: './travel-toast.component.html',
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0 })),
      state('active', style({ opacity: 1 })),
      state('removed', style({ opacity: 0 })),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}'))
    ])
  ],
  preserveWhitespaces: false,
  standalone: false
})
export class TravelToastComponent implements OnDestroy {

  public message?: string | null;
  public title?: string;
  public options: IndividualConfig;
  public duplicatesCount!: number;
  public originalTimeout: number;
  public width: number = -1;
  public snackbarType = SnackbarType.Notification;

  @HostBinding('@flyInOut') state: {
    value: string;
    params: { easeTime: string | number; easing: string; };
  } = {
      value: 'inactive',
      params: { easeTime: this.toastPackage.config.easeTime, easing: 'ease-in' }
    };

  @HostBinding('style.display') get displayStyle(): string | undefined {
    if (this.state.value === 'inactive') {
      return 'none';
    }
    return;
  }

  private timeout: any;
  private intervalId: any;

  private hideTime!: number;

  private subscriptions: Subscription = new Subscription();

  @HostListener('mouseenter') stickAround(): void {
    if (this.state.value === 'removed') {
      return;
    }
    clearTimeout(this.timeout);
    this.options.timeOut = 0;
    this.hideTime = 0;

    clearInterval(this.intervalId);
    this.width = 0;
  }

  @HostListener('mouseleave') delayedHideToast(): void {
    if (
      (this.options.disableTimeOut === true || this.options.disableTimeOut === 'extendedTimeOut') ||
      this.options.extendedTimeOut === 0 ||
      this.state.value === 'removed'
    ) {
      return;
    }
    this.outsideTimeout(() => this.remove(), this.options.extendedTimeOut);
    this.options.timeOut = this.options.extendedTimeOut;
    this.hideTime = new Date().getTime() + (this.options.timeOut || 0);
    this.width = -1;
    if (this.options.progressBar) {
      this.outsideInterval(() => this.updateProgress(), 10);
    }
  }

  constructor(
    private toastPackage: ToastPackage,
    private appFacade: AppFacade,
    private toastrService: ToastrService,
    private ngZone: NgZone,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    if (this.isBrowser) {
      this.message = toastPackage.message;
      this.title = toastPackage.title;
      this.options = toastPackage.config;
      this.originalTimeout = toastPackage.config.timeOut;
      this.snackbarType = toastPackage?.toastType as SnackbarType;
      this.subscriptions.add(
        toastPackage.toastRef.afterActivate().subscribe(() => {
          this.activateToast();
        })
      );
      this.subscriptions.add(
        toastPackage.toastRef.manualClosed().subscribe(() => {
          this.remove();
        })
      );
      this.subscriptions.add(
        toastPackage.toastRef.timeoutReset().subscribe(() => {
          this.resetTimeout();
        })
      );
      this.subscriptions.add(
        toastPackage.toastRef.countDuplicate().subscribe(count => {
          this.duplicatesCount = count;
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    clearInterval(this.intervalId);
    clearTimeout(this.timeout);
  }

  get showToast$(): Observable<boolean> {
    return this.toastr$.pipe(map(toastr => !!toastr && !!this.isBrowser));
  }

  get showMessageByHtmlFormat(): boolean {
    return this.message && this.options.enableHtml;
  }

  get showMessageBySimpleString(): boolean {
    return this.message && !this.options.enableHtml;
  }

  private get toastr$(): Observable<string> {
    return this.appFacade.toastr$;
  }

  public remove(): void {
    if (this.state.value === 'removed') {
      return;
    }
    clearTimeout(this.timeout);
    this.state = { ...this.state, value: 'removed' };
    this.outsideTimeout(
      () => this.toastrService.remove(this.toastPackage.toastId),
      +this.toastPackage.config.easeTime
    );
  }

  public clickSnackbar(): void {
    if (this.state.value === 'removed') {
      return;
    }
    this.toastPackage.triggerTap();
    if (this.options.tapToDismiss) {
      this.remove();
    }
  }

  private activateToast(): void {
    this.state = { ...this.state, value: 'active' };
    if (!(this.options.disableTimeOut === true || this.options.disableTimeOut === 'timeOut') && this.options.timeOut) {
      this.outsideTimeout(() => this.remove(), this.options.timeOut);
      this.hideTime = new Date().getTime() + this.options.timeOut;
      if (this.options.progressBar) {
        this.outsideInterval(() => this.updateProgress(), 10);
      }
    }
  }

  private updateProgress(): void {
    if (this.width === 0 || this.width === 100 || !this.options.timeOut) {
      return;
    }
    const now = new Date().getTime();
    const remaining = this.hideTime - now;
    this.width = (remaining / this.options.timeOut) * 100;
    if (this.options.progressAnimation === 'increasing') {
      this.width = 100 - this.width;
    }
    if (this.width <= 0) {
      this.width = 0;
    }
    if (this.width >= 100) {
      this.width = 100;
    }
  }

  private resetTimeout(): void {
    clearTimeout(this.timeout);
    clearInterval(this.intervalId);
    this.state = { ...this.state, value: 'active' };
    this.outsideTimeout(() => this.remove(), this.originalTimeout);
    this.options.timeOut = this.originalTimeout;
    this.hideTime = new Date().getTime() + (this.options.timeOut || 0);
    this.width = -1;
    if (this.options.progressBar) {
      this.outsideInterval(() => this.updateProgress(), 10);
    }
  }

  private outsideTimeout(func: () => any, timeout: number): void {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(
        () =>
          (this.timeout = setTimeout(
            () => this.runInsideAngular(func),
            timeout
          ))
      );
    } else {
      this.timeout = setTimeout(() => func(), timeout);
    }
  }

  private outsideInterval(func: () => any, timeout: number): void {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(
        () =>
          (this.intervalId = setInterval(
            () => this.runInsideAngular(func),
            timeout
          ))
      );
    } else {
      this.intervalId = setInterval(() => func(), timeout);
    }
  }

  private runInsideAngular(func: () => any): void {
    if (this.ngZone) {
      this.ngZone.run(() => func());
    } else {
      func();
    }
  }
}
